import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';

const STORAGE_KEY = 'activeTabStates';

/**
 * ActiveTabContext
 *
 * Preserves active tab state across navigation and page refresh within a session.
 * Stores tab states per page (keyed by page identifier) and syncs to sessionStorage.
 * State is cleared when the browser tab is closed (sessionStorage behavior).
 *
 * Usage in components:
 * const { activeTab, setActiveTab } = useActiveTab(TAB_STATE_KEYS.ADMIN_APPROVALS);
 */
const ActiveTabContext = createContext({
  getActiveTab: () => 0,
  setActiveTab: () => {},
});

const getStoredTabStates = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveTabStates = (states) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(states));
  } catch {
    // sessionStorage might be full or disabled
  }
};

export const ActiveTabProvider = ({ children }) => {
  const [tabStates, setTabStates] = useState(() => getStoredTabStates());

  useEffect(() => {
    saveTabStates(tabStates);
  }, [tabStates]);

  const getActiveTab = useCallback(
    (pageKey) => {
      return tabStates[pageKey] ?? 0;
    },
    [tabStates]
  );

  const setActiveTab = useCallback((pageKey, tabIndex) => {
    setTabStates((prev) => ({
      ...prev,
      [pageKey]: tabIndex,
    }));
  }, []);

  return (
    <ActiveTabContext.Provider value={{ getActiveTab, setActiveTab }}>
      {children}
    </ActiveTabContext.Provider>
  );
};

/**
 * Custom hook to manage active tab state for a specific page.
 *
 * @param {string} pageKey - Unique identifier for the page (from TAB_STATE_KEYS)
 * @returns {{ activeTab: number, setActiveTab: (tabIndex: number) => void }}
 */
export const useActiveTab = (pageKey) => {
  const context = useContext(ActiveTabContext);
  if (!context) {
    throw new Error('useActiveTab must be used within an ActiveTabProvider');
  }

  const { getActiveTab, setActiveTab: contextSetActiveTab } = context;

  const activeTab = getActiveTab(pageKey);

  const setActiveTab = useCallback(
    (tabIndex) => {
      contextSetActiveTab(pageKey, tabIndex);
    },
    [contextSetActiveTab, pageKey]
  );

  return { activeTab, setActiveTab };
};

export default ActiveTabContext;
